import type { AdminConsoleSection } from '@/lib/auth/admin-console-access';
import type { ApplicationClassification } from '@/lib/api/types';
import { ROUTES } from '@/router/routes';

export const SUPPORTED_WORKSPACE_CLIENT_IDS = ['admin-bff', 'oss-integration', 'gestion-access', 'declaration-sommaire', 'oss-referential-service', 'oss-maritime-operations', 'oss-ens'] as const;

export type WorkspaceClientId = (typeof SUPPORTED_WORKSPACE_CLIENT_IDS)[number];

export interface WorkspaceNavItem {
  readonly label: string;
  readonly to: string;
  readonly icon: string;
  readonly end?: boolean;
  readonly section?: AdminConsoleSection;
  readonly requiredPermissions?: readonly string[];
}

export interface WorkspaceNavGroup {
  readonly label?: string;
  readonly items: readonly WorkspaceNavItem[];
}

export interface ShellAppContext {
  readonly clientId: WorkspaceClientId;
  readonly routeBase: string;
  readonly homePath: string;
  readonly activeOrganizationId: string | null;
}

export interface PlatformApplicationManifest {
  readonly clientId: WorkspaceClientId;
  readonly domainKey: string;
  readonly classification: ApplicationClassification;
  readonly routeBase: string;
  readonly label: string;
  readonly badgeLabel: string;
  readonly homePath: string;
  readonly navigationMode: 'hosted' | 'remote';
  readonly navGroups: readonly WorkspaceNavGroup[];
  readonly bottomNav: readonly WorkspaceNavItem[];
  readonly remote?: {
    readonly name: string;
    readonly entryUrlEnv: string;
    readonly exposedModule: string;
  };
}

const ICONS = {
  dashboard: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
  users: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
  organizations: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
  authorization: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  applications: 'M4.75 5.75A2.25 2.25 0 017 3.5h10A2.25 2.25 0 0119.25 5.75v4A2.25 2.25 0 0117 12H7a2.25 2.25 0 01-2.25-2.25v-4zM4.75 14.25A2.25 2.25 0 017 12h4A2.25 2.25 0 0113.25 14.25v4A2.25 2.25 0 0111 20.5H7a2.25 2.25 0 01-2.25-2.25v-4zM15.75 15.5h3.5m-1.75-1.75v3.5',
  audit: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
  profile: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
  integration: 'M7.5 7.5h9m-9 4.5h9m-9 4.5h5.25M3.75 6A2.25 2.25 0 016 3.75h12A2.25 2.25 0 0120.25 6v12A2.25 2.25 0 0118 20.25H6A2.25 2.25 0 013.75 18V6z',
  document: 'M7.5 4.5h6l4.5 4.5v10.5A2.25 2.25 0 0115.75 21.75h-8.5A2.25 2.25 0 015 19.5v-12A2.25 2.25 0 017.25 5.25z',
  spark: 'M13.5 2.25l-1.91 5.477H6.75l4.08 2.973-1.56 5.05 4.23-3.07 4.23 3.07-1.56-5.05 4.08-2.973h-4.84L13.5 2.25z',
  partners: 'M16.5 7.5a3 3 0 11-6 0 3 3 0 016 0zm-9.75 11.25a4.5 4.5 0 019 0v.75h-9v-.75zm11.25-2.25a3 3 0 100-6 3 3 0 000 6zm1.5 3a4.5 4.5 0 00-3.16-4.295 5.756 5.756 0 013.16 4.295z',
  mapping: 'M4.5 6.75h6m-6 5.25h6m3-5.25h6m-6 5.25h6M10.5 6.75l3 5.25m0 0l-3 5.25m3-5.25h-3',
  template: 'M6.75 4.5h10.5A2.25 2.25 0 0119.5 6.75v10.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 17.25V6.75A2.25 2.25 0 016.75 4.5zm.75 3h9m-9 3h5.25m-5.25 3h9',
  validation: 'M9 12.75l2.25 2.25L15 9.75m4.5 2.25a7.5 7.5 0 11-15 0 7.5 7.5 0 0115 0z',
  blueprint: 'M4.5 6.75A2.25 2.25 0 016.75 4.5h10.5A2.25 2.25 0 0119.5 6.75v10.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 17.25V6.75zm4.5 2.25h6m-6 3h6m-6 3h3.75',
  briefcase: 'M9 6.75V6a3 3 0 013-3h0a3 3 0 013 3v.75m4.5 0v9.75A2.25 2.25 0 0117.25 18.75H6.75A2.25 2.25 0 014.5 16.5V6.75A2.25 2.25 0 016.75 4.5h10.5A2.25 2.25 0 0119.5 6.75zm-10.5 0h6',
  playground: 'M14.25 4.5l5.25 5.25m-13.5 9.75h3.75l9-9a1.5 1.5 0 10-2.121-2.121l-9 9V21z',
  shield: 'M12 3l7.5 3v5.25c0 4.26-2.792 8.157-7.5 9.75-4.708-1.593-7.5-5.49-7.5-9.75V6L12 3zm-1.125 10.125L9.75 12l-.75.75 1.875 1.875L15 10.5l-.75-.75-3.375 3.375z',
  ship: 'M3.75 14.25l2.25-1.5 2.25 1.5 2.25-1.5 2.25 1.5 2.25-1.5 2.25 1.5 2.25-1.5v4.5a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-4.5zm3-5.25h10.5l-1.5-3h-7.5l-1.5 3z',
  vesselRegistry: 'M4.5 6.75A2.25 2.25 0 016.75 4.5h10.5A2.25 2.25 0 0119.5 6.75v10.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 17.25V6.75zm4.5 3h6m-6 3h6m-6 3h3.75',
  plusCircle: 'M12 4.5a7.5 7.5 0 100 15 7.5 7.5 0 000-15zm0 3.75v7.5m3.75-3.75h-7.5',
  calendar: 'M6.75 3.75v2.25m10.5-2.25v2.25M4.5 8.25h15m-13.5 3h3m-3 3h3m3-3h3m-3 3h3m3-3h.008v.008H18v-.008zm0 3h.008v.008H18v-.008zM5.25 5.25h13.5A1.5 1.5 0 0120.25 6.75v11.5a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5V6.75a1.5 1.5 0 011.5-1.5z',
  clock: 'M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z',
  wave: 'M2.25 15.75c1.5 0 1.5-1.5 3-1.5s1.5 1.5 3 1.5 1.5-1.5 3-1.5 1.5 1.5 3 1.5 1.5-1.5 3-1.5 1.5 1.5 3 1.5M2.25 11.25c1.5 0 1.5-1.5 3-1.5s1.5 1.5 3 1.5 1.5-1.5 3-1.5 1.5 1.5 3 1.5 1.5-1.5 3-1.5 1.5 1.5 3 1.5',
  route: 'M5.25 6.75h9.75m-9.75 4.5h6.75m-6.75 4.5h9.75M4.5 4.5h11.25A2.25 2.25 0 0118 6.75v10.5A2.25 2.25 0 0115.75 19.5H4.5A2.25 2.25 0 012.25 17.25V6.75A2.25 2.25 0 014.5 4.5zm13.5 3.75l3 3-3 3',
  note: 'M6 4.5h8.25L18 8.25V19.5a1.5 1.5 0 01-1.5 1.5h-9A1.5 1.5 0 016 19.5v-15zm8.25 0v3.75H18',
  inbox: 'M3.75 6.75A2.25 2.25 0 016 4.5h12a2.25 2.25 0 012.25 2.25v10.5A2.25 2.25 0 0118 19.5H6a2.25 2.25 0 01-2.25-2.25V6.75zm0 7.5h3.375l1.5 2.25h6.75l1.5-2.25h3.375',
  cog: 'M10.5 4.5h3l.45 2.1a6.8 6.8 0 011.65.675l1.8-1.2 2.1 2.1-1.2 1.8c.3.525.525 1.05.675 1.65l2.1.45v3l-2.1.45a6.8 6.8 0 01-.675 1.65l1.2 1.8-2.1 2.1-1.8-1.2a6.8 6.8 0 01-1.65.675l-.45 2.1h-3l-.45-2.1a6.8 6.8 0 01-1.65-.675l-1.8 1.2-2.1-2.1 1.2-1.8a6.8 6.8 0 01-.675-1.65l-2.1-.45v-3l2.1-.45c.15-.6.375-1.125.675-1.65l-1.2-1.8 2.1-2.1 1.8 1.2a6.8 6.8 0 011.65-.675l.45-2.1zm1.5 6a3 3 0 100 6 3 3 0 000-6z',
} as const;

export const SHARED_BOTTOM_NAV: readonly WorkspaceNavItem[] = [
  { label: 'My Profile', to: ROUTES.PROFILE, icon: ICONS.profile, section: 'profile' },
  { label: 'Preferences', to: ROUTES.PREFERENCES, icon: ICONS.cog },
];

export const APPLICATION_MANIFESTS: Record<WorkspaceClientId, PlatformApplicationManifest> = {
  'admin-bff': {
    clientId: 'admin-bff',
    domainKey: 'platform',
    classification: 'INTERNAL',
    routeBase: '/dashboard',
    label: 'Admin Console',
    badgeLabel: 'Admin Console',
    homePath: ROUTES.DASHBOARD,
    navigationMode: 'hosted',
    navGroups: [
      {
        items: [
          { label: 'Dashboard', to: ROUTES.DASHBOARD, icon: ICONS.dashboard, end: true, section: 'dashboard' },
        ],
      },
      {
        label: 'Identity Management',
        items: [
          { label: 'Users', to: ROUTES.USERS, icon: ICONS.users, section: 'users' },
          { label: 'Organizations', to: ROUTES.ORGANIZATIONS, icon: ICONS.organizations, section: 'organizations' },
        ],
      },
      {
        label: 'Access Control',
        items: [
          { label: 'Authorization', to: ROUTES.AUTHORIZATION, icon: ICONS.authorization, section: 'authorization' },
          { label: 'Applications', to: ROUTES.APPLICATIONS, icon: ICONS.applications, section: 'applications' },
        ],
      },
      {
        label: 'Compliance',
        items: [
          { label: 'Audit Log', to: ROUTES.AUDIT, icon: ICONS.audit, section: 'audit' },
        ],
      },
    ],
    bottomNav: SHARED_BOTTOM_NAV,
  },
  'oss-integration': {
    clientId: 'oss-integration',
    domainKey: 'integration',
    classification: 'SHARED',
    routeBase: ROUTES.INTEGRATION_BASE,
    label: 'OSS Integration',
    badgeLabel: 'OSS Integration',
    homePath: ROUTES.INTEGRATION_HOME,
    navigationMode: 'hosted',
    navGroups: [
      {
        label: 'Daily work',
        items: [
          { label: 'Home', to: ROUTES.INTEGRATION_DASHBOARD, icon: ICONS.dashboard, end: true },
          { label: 'Set up integration', to: ROUTES.INTEGRATION_FLOW_BUILDER, icon: ICONS.integration },
          { label: 'Messages', to: ROUTES.INTEGRATION_DOCUMENTS, icon: ICONS.document },
          { label: 'Monitoring', to: ROUTES.INTEGRATION_EXECUTIONS, icon: ICONS.audit },
          { label: 'Command Center', to: ROUTES.INTEGRATION_COMMAND_CENTER, icon: ICONS.dashboard },
          { label: 'Recovery', to: ROUTES.INTEGRATION_DLQ, icon: ICONS.spark },
          { label: 'Maritime', to: ROUTES.INTEGRATION_MARITIME, icon: ICONS.ship },
        ],
      },
      {
        label: 'EAI Studio',
        items: [
          { label: 'Message Schemas', to: ROUTES.INTEGRATION_SCHEMAS, icon: ICONS.blueprint },
          { label: 'Mapping Designer', to: ROUTES.INTEGRATION_MAPPING_DESIGNER, icon: ICONS.mapping },
          { label: 'Test Lab', to: ROUTES.INTEGRATION_TEST_LAB, icon: ICONS.playground },
          { label: 'Publish Center', to: ROUTES.INTEGRATION_PUBLISH_CENTER, icon: ICONS.spark },
        ],
      },
      {
        label: 'Advanced setup',
        items: [
          { label: 'Flow catalog', to: ROUTES.INTEGRATION_FLOWS, icon: ICONS.integration },
          { label: 'Delivery channels', to: ROUTES.INTEGRATION_CONNECTOR_CONSOLE, icon: ICONS.route },
          { label: 'Partners', to: ROUTES.INTEGRATION_PARTNERS, icon: ICONS.partners },
          { label: 'Secrets', to: ROUTES.INTEGRATION_SECRETS, icon: ICONS.shield },
          { label: 'Mappings', to: ROUTES.INTEGRATION_MAPPINGS, icon: ICONS.mapping },
          { label: 'Templates', to: ROUTES.INTEGRATION_TEMPLATES, icon: ICONS.template },
          { label: 'Validation rules', to: ROUTES.INTEGRATION_VALIDATION_RULES, icon: ICONS.validation },
          { label: 'Inbound policies', to: ROUTES.INTEGRATION_INBOUND_POLICIES, icon: ICONS.shield },
          { label: 'Generate document', to: ROUTES.INTEGRATION_GENERATE, icon: ICONS.spark },
          { label: 'Exchange log', to: ROUTES.INTEGRATION_EXCHANGES, icon: ICONS.clock },
          { label: 'Settings', to: ROUTES.INTEGRATION_SETTINGS, icon: ICONS.cog },
        ],
      },
      {
        label: 'AS2 Gateway',
        items: [
          { label: 'AS2 overview', to: ROUTES.INTEGRATION_AS2, icon: ICONS.dashboard, end: true },
          { label: 'Outbound messages', to: ROUTES.INTEGRATION_AS2_OUTBOUND_MESSAGES, icon: ICONS.document },
          { label: 'Inbound messages', to: ROUTES.INTEGRATION_AS2_INBOUND_MESSAGES, icon: ICONS.document },
          { label: 'Dead-letter queue', to: ROUTES.INTEGRATION_AS2_DLQ, icon: ICONS.spark },
          { label: 'Certificates', to: ROUTES.INTEGRATION_AS2_CERTIFICATES, icon: ICONS.shield },
          { label: 'Transport profiles', to: ROUTES.INTEGRATION_AS2_TRANSPORT_PROFILES, icon: ICONS.partners },
        ],
      },
    ],
    bottomNav: SHARED_BOTTOM_NAV,
    remote: {
      name: 'ossIntegrationAdmin',
      entryUrlEnv: 'OSS_REMOTE_OSS_INTEGRATION_ADMIN_URL',
      exposedModule: 'IntegrationAdminApp',
    },
  },
  'gestion-access': {
    clientId: 'gestion-access',
    domainKey: 'access',
    classification: 'INTERNAL',
    routeBase: ROUTES.GESTION_ACCESS_BASE,
    label: 'Gestion des Acces',
    badgeLabel: 'Gestion Acces',
    homePath: ROUTES.GESTION_ACCESS_HOME,
    navigationMode: 'hosted',
    navGroups: [],
    bottomNav: SHARED_BOTTOM_NAV,
    remote: {
      name: 'gestionAccessApp',
      entryUrlEnv: 'OSS_REMOTE_GESTION_ACCESS_APP_URL',
      exposedModule: 'GestionAccessApp',
    },
  },
  'declaration-sommaire': {
    clientId: 'declaration-sommaire',
    domainKey: 'customs',
    classification: 'FLOW',
    routeBase: ROUTES.DECLARATION_SOMMAIRE_BASE,
    label: 'Declaration Sommaire',
    badgeLabel: 'Declaration Sommaire',
    homePath: ROUTES.DECLARATION_SOMMAIRE_HOME,
    navigationMode: 'hosted',
    navGroups: [],
    bottomNav: SHARED_BOTTOM_NAV,
  },
  'oss-referential-service': {
    clientId: 'oss-referential-service',
    domainKey: 'referential',
    classification: 'INTERNAL',
    routeBase: ROUTES.REFERENTIAL_BASE,
    label: 'Referentials',
    badgeLabel: 'Referentials',
    homePath: ROUTES.REFERENTIAL_HOME,
    navigationMode: 'hosted',
    navGroups: [
      {
        items: [
          { label: 'Items', to: ROUTES.REFERENTIAL_ITEMS, icon: ICONS.vesselRegistry },
          { label: 'Categories', to: ROUTES.REFERENTIAL_CATEGORIES, icon: ICONS.blueprint },
          { label: 'Domains', to: ROUTES.REFERENTIAL_DOMAINS, icon: ICONS.applications },
          { label: 'Imports', to: ROUTES.REFERENTIAL_IMPORTS, icon: ICONS.document },
          { label: 'Customs Operators', to: ROUTES.REFERENTIAL_CUSTOMS_OPERATORS, icon: ICONS.briefcase },
          { label: 'Operator Admin', to: ROUTES.REFERENTIAL_OPERATORS, icon: ICONS.shield },
          { label: 'Package Types', to: ROUTES.REFERENTIAL_PACKAGE_TYPES, icon: ICONS.template },
          { label: 'Storage Locations', to: ROUTES.REFERENTIAL_STORAGE_LOCATIONS, icon: ICONS.inbox },
          { label: 'Validate', to: ROUTES.REFERENTIAL_VALIDATE, icon: ICONS.validation },
          { label: 'Lookups', to: ROUTES.REFERENTIAL_LOOKUPS, icon: ICONS.integration },
        ],
      },
    ],
    bottomNav: SHARED_BOTTOM_NAV,
  },
  'oss-maritime-operations': {
    clientId: 'oss-maritime-operations',
    domainKey: 'maritime',
    classification: 'COMMUNITY',
    routeBase: ROUTES.MARITIME_BASE,
    label: 'Maritime Operations',
    badgeLabel: 'Maritime',
    homePath: ROUTES.MARITIME_HOME,
    navigationMode: 'hosted',
    navGroups: [
      {
        items: [
          { label: 'Dashboard', to: ROUTES.MARITIME_DASHBOARD, icon: ICONS.dashboard, requiredPermissions: ['MARITIME.DASHBOARD.VIEW'] },
          { label: 'Vessel Calls', to: ROUTES.MARITIME_VESSEL_CALLS, icon: ICONS.ship, requiredPermissions: ['MARITIME.VESSEL-CALL.VIEW'] },
          { label: 'Movements', to: ROUTES.MARITIME_MOVEMENTS, icon: ICONS.route, requiredPermissions: ['MARITIME.MOVEMENT.VIEW'] },
          { label: 'Marine Services', to: ROUTES.MARITIME_SERVICES, icon: ICONS.wave, requiredPermissions: ['MARITIME.SERVICE.VIEW'] },
        ],
      },
      {
        label: 'Operations',
        items: [
          { label: 'Planning', to: ROUTES.MARITIME_PLANNING, icon: ICONS.calendar, requiredPermissions: ['MARITIME.PLANNING.VIEW'] },
          { label: 'Vessels', to: ROUTES.MARITIME_VESSELS, icon: ICONS.vesselRegistry, requiredPermissions: ['MARITIME.VESSEL.READ'] },
          { label: 'Tracking', to: ROUTES.MARITIME_TRACKING, icon: ICONS.clock, requiredPermissions: ['MARITIME.TRACKING.VIEW'] },
          { label: 'Documents', to: ROUTES.MARITIME_DOCUMENTS, icon: ICONS.document, requiredPermissions: ['MARITIME.DOCUMENT.VIEW'] },
        ],
      },
    ],
    bottomNav: SHARED_BOTTOM_NAV,
  },
  'oss-ens': {
    clientId: 'oss-ens',
    domainKey: 'customs',
    classification: 'FLOW',
    routeBase: ROUTES.ENS_BASE,
    label: 'Déclarations ENS',
    badgeLabel: 'ENS',
    homePath: ROUTES.ENS_HOME,
    navigationMode: 'hosted',
    navGroups: [],
    bottomNav: SHARED_BOTTOM_NAV,
  },
};

// TODO: Future operational governance feature.
// TODO: Future integration monitoring feature.

export function resolveManifestForPath(pathname: string): PlatformApplicationManifest {
  const manifests = Object.values(APPLICATION_MANIFESTS).sort(
    (left, right) => right.routeBase.length - left.routeBase.length,
  );

  return manifests.find((manifest) => pathname.startsWith(manifest.routeBase)) ?? APPLICATION_MANIFESTS['admin-bff'];
}
